import {
	Box,
	Button,
	Grid,
	Icon,
	MenuItem,
	Pagination,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TableHead,
	TableRow,
	TextField,
	Typography
} from "@mui/material";
import { addDays, endOfMonth, format, isBefore } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ModalContext, ToastContent, ToolbarFinancialHealth } from "../../../../shared/components";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../../shared/enums/profiles";
import { Environment } from "../../../../shared/environment";
import { ProfileGuard } from "../../../../shared/guards/ProfileGuard";
import { useDebounce, useToast } from "../../../../shared/hooks";
import { FinancialService, IListFinancial } from "../../../../shared/services/api/financial/FinancialService";
import { CustomizedAccordions } from "../../../financial/components/TableFinancial";

export type TInvoiceSelect = {
	clientId: string;
	invoiceId: string;
	selected: boolean;
};
let listInvoiceSelect: TInvoiceSelect[] = [];

export const FinancialHealthListTab = () => {
	const toastHandler = useToast();
	const [searchParams, setSearchParams] = useSearchParams();
	const { debounce } = useDebounce();
	const navigate = useNavigate();
	const [rowsFinancial, setRowsFinancial] = useState<IListFinancial[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [totalCountFinancial, setTotalCountFinancial] = useState(0);
	const [stringDocument, setDocument] = useState("");
	const [pesquisar, setPesquisar] = useState(true);
	const [refreshList, setRefreshList] = useState(false);
	const [reportBegin, setReportBegin] = useState("");
	const [reportEnd, setReportEnd] = useState("");
	const [reportType, setReportType] = useState("");
	const [reportOrder, setReportOrder] = useState("nome");
	const [modalReport, setModalReport] = useState(false);

	const pagina = useMemo(() => Number(searchParams.get("pagina") || "1"), [searchParams]);
	const busca = useMemo(() => stringDocument, [stringDocument]);
	const statusInvoice = useMemo(() => searchParams.get("statusInvoice") || "Todos", [searchParams]);
	const limitInvoice = useMemo(() => searchParams.get("limitInvoice") || "06", [searchParams]);
	const limitClient = useMemo(() => searchParams.get("limitClient") || "05", [searchParams]);
	const grouped = useMemo(() => searchParams.get("grouped") || "Todos", [searchParams]);

	const dataIni = useMemo(() => searchParams.get("dataini") || new Date().toISOString().split("T")[0], [searchParams]);
	const dataFim = useMemo(() => searchParams.get("datafim") || endOfMonth(new Date()).toISOString().split("T")[0], [searchParams]);

	const handleBusca = () => {
		setSearchParams({ pagina: "1", busca, statusInvoice, limitInvoice, limitClient, grouped }, { replace: true });
		setPesquisar(!pesquisar);
	};

	const handleExport = async () => {
		setIsLoading(true);
		try {
			const { data } = await FinancialService.getExportInvoiceCsv(busca, statusInvoice);
			const url = window.URL.createObjectURL(new Blob([data]));
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", "exported_data.csv");
			document.body.appendChild(link);
			link.click();
		} catch (error) {
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Erro" content="Não foi possível fazer download" />,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleModalReport = () => {
		setModalReport(true);
		setReportBegin(dataIni);
		setReportEnd(dataFim);
		setReportType("todos");
		setReportOrder("nome");
	};

	const handleReportInvoice = () => {
		setIsLoading(true);
		debounce(() => {
			FinancialService.getReportInvoice(reportType, reportOrder, reportBegin, reportEnd).then((result) => {
				setIsLoading(false);
				if (result instanceof Error) {
					toastHandler.present({
						type: "error",
						position: "top-right",
						messageOrContent: <ToastContent title="Relatório de Boletos" content="Nenhum registro localizado" />,
					});
				}
			});
		});
	};

	const handleConciliation = async () => {
		setIsLoading(true);
		let dataAtual = new Date(dataIni);
		const dataFinal = new Date(dataFim);

		while (!isBefore(dataFinal, dataAtual)) {
			const dataFormatada = format(dataAtual, "yyyy-MM-dd", { locale: ptBR });
			try {
				await FinancialService.getConciliation(dataFormatada, dataFormatada);
			} catch (error) {
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title="Erro" content={String(error)} />,
				});
			}
			dataAtual = addDays(dataAtual, 1);
		}

		setIsLoading(false);
	};

	useEffect(() => {
		handleBusca();
	}, [refreshList]);

	useEffect(() => {
		setIsLoading(true);
		debounce(() => {
			FinancialService.getInvoiceForClient(pagina, limitClient, busca, statusInvoice, limitInvoice, grouped).then((result) => {
				setIsLoading(false);
				if (!(result instanceof Error)) {
					setTotalCountFinancial(result.total);
					setRowsFinancial(result.result);
					listInvoiceSelect = [];
				}
			});
		});
	}, [pesquisar, pagina]);

	return (
		<Box>
			<ToolbarFinancialHealth
				clickBack={() => navigate(`/dashboard/`)}
				onChangeTextSearch={setDocument}
				textSearch={busca}
				onClick={handleBusca}
				onClickExport={handleExport}
				onChangeStatusInvoice={(val) => setSearchParams({ pagina: "1", busca, statusInvoice: val, limitInvoice }, { replace: true })}
				onChangeLimitInvoice={(val) => setSearchParams({ pagina: "1", busca, statusInvoice, limitInvoice: val }, { replace: true })}
				onChangeLimitClient={(val) => setSearchParams({ pagina: "1", busca, statusInvoice, limitInvoice, limitClient: val }, { replace: true })}
				onChangeGroup={(val) => setSearchParams({ pagina: "1", busca, statusInvoice, limitInvoice, limitClient, grouped: val }, { replace: true })}
				onclickReport={handleModalReport}
				onclickConcilied={handleConciliation}
			/>

			{modalReport && (
				<ModalContext
					isCloseModal={() => setModalReport(false)}
					isOpenModal={modalReport}
					messageType="info"
					showButtonCancel={false}
					textButtonConfirm="Fechar"
					ITitle="Relatório de Boletos"
					IMessage={[
						<Box sx={{ width: "80%" }}>
							<Grid container spacing={2} mb={3} mt={2}>
								<Grid item xs={4}>
									<TextField
										select
										label="Tipo de Cliente"
										fullWidth
										size="small"
										defaultValue={reportType}
										onChange={(e) => setReportType(e.target.value)}
									>
										<MenuItem value="todos">Todos</MenuItem>
										<MenuItem value="pagamento">Pago</MenuItem>
										<MenuItem value="vencidos">Vencidos</MenuItem>
										<MenuItem value="avencer">A vencer</MenuItem>
										<MenuItem value="cancelados">Cancelado</MenuItem>
									</TextField>
								</Grid>
								<Grid item xs={3}>
									<TextField
										type="date"
										label="Data Inicial"
										fullWidth
										size="small"
										defaultValue={reportBegin}
										onChange={(e) => setReportBegin(e.target.value)}
									/>
								</Grid>
								<Grid item xs={3}>
									<TextField
										type="date"
										label="Data Final"
										fullWidth
										size="small"
										defaultValue={reportEnd}
										onChange={(e) => setReportEnd(e.target.value)}
									/>
								</Grid>
								<Grid item xs={4}>
									<TextField
										select
										label="Ordem do relatório"
										fullWidth
										size="small"
										defaultValue={reportOrder}
										onChange={(e) => setReportOrder(e.target.value)}
									>
										<MenuItem value="nome">Nome</MenuItem>
										<MenuItem value="vencimento">Vencimento</MenuItem>
										<MenuItem value="status">Status</MenuItem>
									</TextField>
								</Grid>
							</Grid>

							<Grid container spacing={2} justifyContent="center">
								<Button
									variant="contained"
									startIcon={<Icon>search</Icon>}
									onClick={() => {
										setModalReport(false);
										handleReportInvoice();
									}}
								>
									Visualizar
								</Button>

								<Button variant="outlined" startIcon={<Icon>close</Icon>} onClick={() => setModalReport(false)}>
									Fechar
								</Button>
							</Grid>
						</Box>,
					]}
				/>
			)}

			<ProfileGuard requiredRole={PermissionRolesEnum.FinancialOptions} requiredAction={PermissionActionsEnum.View}>
				<TableContainer component={Paper} sx={{ m: 1, p: 3, width:'auto' }}>
					<Table>
						<TableHead>
							<TableRow sx={{ background: "#f5fbfd" }}>
								<TableCell colSpan={12}>
									<Box display="flex" justifyContent="space-between">
										<Typography variant="body1">Cliente</Typography>
									</Box>
								</TableCell>
							</TableRow>
						</TableHead>

						<TableBody>
							<TableRow>
								<TableCell colSpan={12} sx={{ p: 0 }}>
									{rowsFinancial.map((row) => (
										<CustomizedAccordions
											key={row.id}
											id={row.id || ""}
											name={row.name}
											document={row.document}
											invoice_due_date={row.invoice_due_date}
											email={row.email}
											statusClient={row.status} // 👈 ajuste aqui
											invoice={row.invoices} // 👈 ajuste aqui
											openSum={row.openSum}
											openCount={row.openCount}
											pendingCount={row.pendingCount}
											pendingSum={row.pendingSum}
											paidCount={row.paidCount}
											paidSum={row.paidSum}
											overdueCount={row.overdueCount}
											overdueSum={row.overdueSum}
											nameLoading={isLoading}
											listInvoiceSelect={listInvoiceSelect}
											overRefresh={handleBusca}
										/>
									))}
								</TableCell>
							</TableRow>
						</TableBody>

						{totalCountFinancial === 0 && !isLoading && <caption>{Environment.LISTAGEM_VAZIA}</caption>}

						<LoadingComp isopen={isLoading} />
						<TableFooter>
							{totalCountFinancial > 0 && (
								<TableRow>
									<TableCell colSpan={5}>
										<Pagination
											page={pagina}
											count={Math.ceil(totalCountFinancial / Number(limitClient))}
											onChange={(_, newPage) =>
												setSearchParams(
													{
														busca,
														pagina: newPage.toString(),
														statusInvoice,
														limit: limitInvoice,
														limitClient,
														grouped,
													},
													{ replace: true }
												)
											}
										/>
									</TableCell>
									<TableCell colSpan={4}>
										<Typography>
											Exibindo {pagina * Number(limitClient) - (Number(limitClient) - 1)} -{" "}
											{Math.min(pagina * Number(limitClient), totalCountFinancial)} de{" "}
											<strong style={{ color: "red" }}>{totalCountFinancial}</strong> registro(s)
										</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableFooter>
					</Table>
				</TableContainer>
			</ProfileGuard>
		</Box>
	);
};
